import { useEffect, useState } from 'react';
import type { UtxoBlockListItem } from '../api/types';
import { useBlocks } from '../hooks/useBlocks';
import { BlockCard } from '../components/BlockCard';
import { ErrorMessage } from '../components/ErrorMessage';

const BLOCKS_PER_PAGE =
  Number(import.meta.env.VITE_BLOCKS_PER_PAGE) || 20;

export const HomePage = () => {
  const [blocks, setBlocks] = useState<UtxoBlockListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [startHeight, setStartHeight] = useState<number | null>(null);
  const [nextStartHeight, setNextStartHeight] = useState<number | null>(null);

  const { data, loading, error } = useBlocks(BLOCKS_PER_PAGE + 1, {
    startHeight: startHeight ?? undefined,
  });

  useEffect(() => {
    if (!data) return;

    const pageBlocks = data.blocks || [];
    if (pageBlocks.length === 0) {
      if (startHeight == null) {
        setBlocks([]);
        setHasMore(false);
      }
      setInitialLoad(false);
      setNextStartHeight(null);
      return;
    }

    const hasExtra = pageBlocks.length > BLOCKS_PER_PAGE;
    const visible = hasExtra ? pageBlocks.slice(0, BLOCKS_PER_PAGE) : pageBlocks;

    setBlocks(prev =>
      startHeight == null ? visible : [...prev, ...visible]
    );
    setHasMore(hasExtra);
    setInitialLoad(false);

    const lastVisible = visible[visible.length - 1];
    if (hasExtra && lastVisible) {
      setNextStartHeight(lastVisible.height - 1);
    } else {
      setNextStartHeight(null);
    }
  }, [data]);

  const handleLoadMore = () => {
    if (loading || !hasMore || nextStartHeight == null) return;
    setStartHeight(nextStartHeight);
  };

  if (loading && initialLoad) {
    return (
      <div id="blocks-list">
        {Array.from({ length: 3 }).map((_, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="block-card skeleton" style={{ height: 120 }} />
        ))}
      </div>
    );
  }

  if (error && blocks.length === 0) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <div id="blocks-list">
        {blocks.length === 0 && !loading ? (
          <div className="error">No blocks found</div>
        ) : (
          blocks.map(block => <BlockCard key={block.hash} block={block} />)
        )}
      </div>
      {error && blocks.length > 0 && <ErrorMessage error={error} />}
      <div
        id="load-more-container"
        style={{
          textAlign: 'center',
          margin: '20px 0',
          display: hasMore ? 'block' : 'none',
        }}
      >
        <button
          id="load-more-btn"
          className="load-more-button"
          onClick={handleLoadMore}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Load More Blocks'}
        </button>
      </div>
    </>
  );
};
